import { Builder } from '@builder.io/react'
import CdnImage from 'components/util/CdnImage'
// import CloudinaryImage from 'components/util/CloudinaryImage'
import { Link, graphql, useStaticQuery } from 'gatsby'
import React, { useEffect, useState } from 'react'

import LinkButton, { LinkButtonProp } from '../common/button'
import ComboBox from '../common/combo-box'

import './index.css'

import mobileArrows from 'images/mobile-recipe-book-arrows.svg'
import leftArrow from 'images/recipe-book-left-arrow.svg'
import rightArrow from 'images/recipe-book-right-arrow.svg'

export interface IRecipeBookProps {
  backgroundColor: string
  bookBackgroundColor: string
  bookTextColor: string
  bookDividerColor: string
  buttonEnabled: boolean
  buttonProp: LinkButtonProp
}

interface IRecipeBookItem {
  data: any
  id: string
  name: string
  options: any
}

const RecipeBook = ({
  backgroundColor,
  bookBackgroundColor,
  bookTextColor,
  bookDividerColor,
  buttonEnabled,
  buttonProp
}: IRecipeBookProps) => {
  const [categories, setCategories] = useState<{ key: string; value: string }[]>([])
  const [recipeMap, setRecipeMap] = useState<Map<string, IRecipeBookItem[]>>()
  const [selectedCat, setSelectedCat] = useState<{ key: string; value: string }>()
  const [selectedCatRecipes, setSelectedCatRecipes] = useState<IRecipeBookItem[]>([])
  const [selectedRecipe, setSelectedRecipe] = useState<IRecipeBookItem>()

  const [isAtStartOfRecipeContainer, setIsAtStartOfRecipeContainer] = useState(true)
  const [isAtEndOfRecipeContainer, setIsAtEndOfRecipeContainer] = useState(false)

  const { allBuilderModels }: any = useStaticQuery(graphql`
    query {
      allBuilderModels {
        recipe {
          content
        }
        recipeCategory {
          id
          data {
            key
            value
          }
        }
      }
    }
  `)

  useEffect(() => {
    if (allBuilderModels) {
      let catKeyValuePairArr: any[] = []
      catKeyValuePairArr.push({
        key: 'all',
        value: 'All'
      })

      const _catKeyValueArr = allBuilderModels.recipeCategory.map((cat: any) => {
        return {
          key: cat.data.key,
          value: cat.data.value
        }
      })
      catKeyValuePairArr = [...catKeyValuePairArr, ..._catKeyValueArr]
      setCategories([...catKeyValuePairArr])

      const _recipes: any[] = allBuilderModels.recipe.map((recipe: any) => {
        const { content } = recipe
        const { data } = content
        const { blocks } = data
        const { component } = blocks ? blocks[0] : { component: {} }
        const componentOptions = component.options

        return {
          id: content.id,
          name: content.name,
          data: data,
          options: componentOptions
        }
      })

      const _recipeMap = new Map<string, IRecipeBookItem[]>()
      for (const recipeCat of allBuilderModels.recipeCategory) {
        const categoryId = recipeCat.id
        const categoryName = recipeCat.data.key

        const categoryRecipes = _recipes.filter((r: any) => categoryId === r.data.recipeCategory.id)
        _recipeMap.set(categoryName, categoryRecipes)
      }
      setRecipeMap(_recipeMap)
    }
  }, [allBuilderModels])

  /**
   * intially set all as the category type
   */
  useEffect(() => {
    handleCategoryChange('all')
  }, [categories, recipeMap])

  const handleCategoryChange = (key: string) => {
    const _selectedCat = categories.filter((it: any) => it.key === key)[0]
    setSelectedCat(_selectedCat)

    if (recipeMap) {
      if (key !== 'all') {
        const recipesToCategoryKey = recipeMap.get(key)

        if (recipesToCategoryKey) {
          setSelectedCatRecipes([...recipesToCategoryKey])
          setSelectedRecipe({ ...recipesToCategoryKey[0] })
        }
      } else {
        let allRecipes: any[] = []
        for (const key of recipeMap.keys()) {
          const value = recipeMap.get(key)
          if (value) {
            for (const item of value) {
              allRecipes.push(item)
            }
          }
        }
        setSelectedCatRecipes([...allRecipes])
        setSelectedRecipe({ ...allRecipes[0] })
      }
    }
  }

  /**
   * actual categories that would render all the time
   * since they are master data
   */
  const renderCategories = () => {
    if (!categories) return null

    const categoryItemArr = []
    for (let cat of categories) {
      categoryItemArr.push(
        <button
          className={`mr-2 mb-2 rounded-3xl border-2 border-transparent bg-white px-6 py-3 text-primary ${
            selectedCat?.key === cat.key ? 'border-2 border-primary' : ''
          }`}
          onClick={() => handleCategoryChange(cat.key)}
          key={cat.key}
        >
          {cat.value}
        </button>
      )
    }

    return (
      <>
        <div className="mx-auto hidden w-2/3 justify-center sm:flex">{categoryItemArr}</div>
        <div className="mx-auto block w-5/6 sm:hidden">
          <ComboBox items={categories} onItemSelected={handleCategoryChange} />
        </div>
      </>
    )
  }

  /**
   * given time render first six recipes out of the selected category
   */
  const renderRecipes = () => {
    if (!selectedCatRecipes) return null

    return selectedCatRecipes?.map((recipe: any, i: number) => {
      return (
        <div
          className={`recipe-preview-item relative z-10 bg-white ${
            recipe === selectedRecipe ? 'border-2 border-primary' : 'border-0'
          } ${
            i + 1 < selectedCatRecipes?.length && 'mr-4 md:mr-8'
          } h-[50px] w-[175px] overflow-hidden rounded-xl md:h-[70px] md:w-[300px] md:rounded-3xl`}
          onClick={() => setSelectedRecipe(recipe)}
        >
          <CdnImage
            className="absolute -left-5 -top-2 z-0 w-[100px] overflow-hidden object-contain md:-left-8 md:-top-8 md:w-[190px]"
            url={recipe?.data?.cdnCookedImage}
            alt={recipe?.data?.title}
            steps={[440]}
          />

          <div className="absolute top-0 bottom-0 right-2 z-10 flex h-full w-3/5 items-center justify-center whitespace-normal text-center">
            <h3 className="text-xs md:text-lg">{recipe?.data?.title}</h3>
          </div>
        </div>
      )
    })
  }

  const renderCTAButton = () => {
    if (buttonEnabled) {
      return (
        <LinkButton
          buttonColor={buttonProp?.buttonColor}
          buttonText={buttonProp?.buttonText}
          loggedInButtonText={buttonProp?.loggedInButtonText}
          buttonTextColor={buttonProp?.buttonTextColor}
          buttonUrl={buttonProp?.buttonUrl}
          isLink={buttonProp.isLink}
          classList={
            'mt-4 mx-auto w-full md:w-fit flex items-center justify-center px-5 py-3 border border-transparent text-base font-medium rounded-full text-indigo-700 bg-indigo-100 hover:bg-indigo-200 md:text-lg'
          }
        />
      )
    }

    return null
  }

  /**
   * recipe ingredient list inside the recipe data component
   */
  const renderIngredientList = (ingredientList: any[]) => {
    if (ingredientList) {
      return ingredientList.map((item: any, i: number) => {
        return (
          <div className="ingredient-item mb-2 flex w-full" key={`${item.ingredientName}-${i}`}>
            <div className="flex w-full flex-col justify-center">
              <p className="text-sm sm:text-base">
                <strong>{item.ingredientName}</strong> - {item.ingredientDescription}
              </p>
            </div>
          </div>
        )
      })
    }
    return null
  }

  const renderRecipeDataView = () => {
    if (!selectedRecipe || !selectedRecipe.options) return null
    console.log('SELECTED RECIPE', selectedRecipe)

    const recipeHero = selectedRecipe.options.recipeHero || {}
    const recipeIngredient = selectedRecipe.options.recipeIngredient || {}
    return (
      <div className="recipe-data-view-wrapper mx-auto block w-11/12 py-4 sm:flex sm:w-full">
        <div className="recipe-main-img w-full sm:w-1/2">
          <CdnImage
            className="h-full w-full object-cover"
            url={recipeHero?.heroImage}
            alt={recipeHero?.recipeTitle}
            steps={[500, 750]}
          />

          {/* {recipeHero?.heroImage ? (
            <CdnImage
              className="h-full w-full object-cover"
              url={recipeHero?.heroImage}
              alt={recipeHero?.recipeTitle}
              steps={[500, 750]}
            />
          ) : (
            <CloudinaryImage
              publicId={recipeHero?.cloudinaryHeroImage.public_id}
              alt={recipeHero?.recipeTitle}
              className="h-full w-full object-cover"
              steps={[500, 750]}
            />
          )} */}
        </div>
        <div
          className="recipe-item-details w-full bg-[#FEF9F5] p-8 sm:w-1/2 sm:bg-white sm:p-20 md:shadow-[inset_20px_0_20px_-10px_rgba(0,0,0,0.4)]"
          style={{ backgroundColor: bookBackgroundColor }}
        >
          <div className="font-third mb-4 text-4xl md:text-7xl" style={{ color: bookTextColor }}>
            {recipeHero.recipeTitle}
          </div>
          <div className="mb-2">
            <h4 className="text-sm sm:text-base" style={{ color: bookTextColor }}>
              {recipeIngredient.mainDescription}
            </h4>
          </div>
          <div className="recipe-book-divider mb-2" style={{ backgroundColor: bookDividerColor }} />
          <div className="w-full">
            <div className="w-full" style={{ color: bookTextColor }}>
              <p className="mr-2 inline-block text-xs font-bold uppercase">Serves</p>
              <p className="inline-block text-xs uppercase">1 hungry pooch</p>
            </div>
            <div className="w-full" style={{ color: bookTextColor }}>
              <p className="mr-2 inline-block text-xs font-bold uppercase">Time taken</p>
              <p className="inline-block text-xs uppercase">Over 100 recipe testings</p>
            </div>
          </div>
          <div className="recipe-book-divider mt-2 mb-4" style={{ backgroundColor: bookDividerColor }} />

          <div className="mb-8 w-full">{renderIngredientList(recipeIngredient.ingredientItems)}</div>
          <div className="w-full">
            <Link to={selectedRecipe.data.url} className="text-sm font-bold sm:text-base">
              Read more about {recipeHero.recipeTitle} →
            </Link>
          </div>
        </div>
      </div>
    )
  }

  useEffect(() => {
    const scrollListenerFn = (e: any) => {
      if (e.target.scrollLeft === 0) {
        setIsAtStartOfRecipeContainer(true)
      } else {
        setIsAtStartOfRecipeContainer(false)
      }

      if (e.target.scrollLeft + e.target.offsetWidth >= e.target.scrollWidth) {
        setIsAtEndOfRecipeContainer(true)
      } else {
        setIsAtEndOfRecipeContainer(false)
      }
    }

    document.querySelector('.recipes-container')?.addEventListener('scroll', scrollListenerFn)

    return () => {
      document.querySelector('.recipes-container')?.removeEventListener('scroll', scrollListenerFn)
    }
  }, [recipeMap])

  const moveToTheRight = () => {
    if (document.querySelector('.recipes-container')) {
      document.querySelector('.recipes-container').scrollLeft += 385
    }
  }

  const moveToTheLeft = () => {
    if (document.querySelector('.recipes-container')) {
      document.querySelector('.recipes-container').scrollLeft -= 385
    }
  }

  return (
    <div className="w-full py-4 sm:py-8" style={{ backgroundColor }}>
      <div className="container mx-auto">
        {renderCategories()}

        <div className="relative flex w-full flex-row">
          <button
            onClick={moveToTheLeft}
            className={`${
              isAtStartOfRecipeContainer && 'cursor-not-allowed opacity-40'
            } absolute left-2 top-0 bottom-0 flex hidden items-center md:left-0 md:block`}
          >
            <img className="h-[35px]" src={leftArrow} alt="Left arrow for recipe book" />
          </button>

          <div className="recipes-container mx-auto flex w-full	overflow-x-auto overflow-y-hidden scroll-smooth whitespace-nowrap py-4 sm:py-8 md:w-[calc(100%-5rem)]">
            <div className="relative mx-auto flex justify-center px-4">{renderRecipes()}</div>
          </div>

          <button
            onClick={moveToTheRight}
            className={`${
              isAtEndOfRecipeContainer && 'cursor-not-allowed opacity-40'
            } absolute right-2 top-0 bottom-0 flex hidden items-center md:right-0 md:block`}
          >
            <img className="h-[35px]" src={rightArrow} alt="Right arrow for recipe book" />
          </button>
        </div>

        <div className="mx-auto flex w-full justify-center opacity-40 sm:hidden">
          <img src={mobileArrows} alt="Scroll through the recipe selection" />
        </div>

        {renderRecipeDataView()}
        {renderCTAButton()}
      </div>
    </div>
  )
}

export default RecipeBook
